import { type ClipSpriteSheet } from '@air/api/types';

import { getClipSpritesheetAspectRatio } from './getClipSpritesheetAspectRatio';
import { getClipSpritesheetFrameCount } from './getClipSpritesheetFrameCount';

export type Dimension = {
  height: number;
  width: number;
};

const isHorizontal = (aspectRatio: number) => aspectRatio > 1;

export const getDimensions = (aspectRatio: number, width: number, height: number) => {
  if (isHorizontal(aspectRatio)) {
    const scaledHeight = height;
    const scaledWidth = scaledHeight * aspectRatio;

    if (scaledWidth < width) {
      return {
        aspectRatio,
        scaledHeight: width / aspectRatio,
        scaledWidth: width,
      };
    }

    return {
      aspectRatio,
      scaledHeight,
      scaledWidth,
    };
  }

  const scaledWidth = width;
  const scaledHeight = scaledWidth * aspectRatio;

  if (scaledHeight > height) {
    return {
      aspectRatio,
      scaledHeight: height,
      scaledWidth: height * aspectRatio,
    };
  }

  return {
    aspectRatio,
    scaledHeight: scaledWidth / aspectRatio,
    scaledWidth,
  };
};

const calculateSpritePosition = ({
  containerWidth,
  containerHeight,
  frame,
  spritesheet,
}: {
  containerWidth: number;
  containerHeight: number;
  frame: number;
  spritesheet: ClipSpriteSheet;
}) => {
  const frameCount = getClipSpritesheetFrameCount(spritesheet);
  const aspectRatio = getClipSpritesheetAspectRatio(spritesheet);
  const dimensions = getDimensions(aspectRatio, containerWidth, containerHeight);

  const frameWidth = Math.floor(dimensions.scaledWidth);
  const frameHeight = Math.floor(dimensions.scaledHeight);

  const currentFrame = frame === frameCount ? frameCount - 1 : frame;
  const column = currentFrame % spritesheet.columns;
  const row = Math.floor(currentFrame / spritesheet.columns);

  const spriteSheetWidth = frameWidth * spritesheet.columns;
  const spriteSheetHeight = frameHeight * spritesheet.rows;
  const backgroundPositionX = -(column * frameWidth);
  const backgroundPositionY = -(row * frameHeight);

  return {
    aspectRatio,
    backgroundPositionX: Math.abs(backgroundPositionX) === 0 ? 0 : backgroundPositionX,
    backgroundPositionY: Math.abs(backgroundPositionY) === 0 ? 0 : backgroundPositionY,
    containerHeight,
    containerWidth,
    frameHeight,
    frameWidth,
    spriteSheetHeight,
    spriteSheetWidth,
  };
};

export const getSpriteStyles = ({
  containerWidth,
  containerHeight,
  spritesheet,
  frame,
}: {
  containerWidth: number;
  containerHeight: number;
  spritesheet: ClipSpriteSheet;
  frame: number;
}) => {
  const { frameWidth, frameHeight, spriteSheetWidth, spriteSheetHeight, backgroundPositionX, backgroundPositionY } =
    calculateSpritePosition({ containerHeight, containerWidth, frame, spritesheet });
  const aspectRatio = getClipSpritesheetAspectRatio(spritesheet);

  return {
    backgroundPosition: `${backgroundPositionX}px ${backgroundPositionY}px`,
    backgroundRepeat: 'no-repeat' as const,
    backgroundSize: `${spriteSheetWidth}px ${spriteSheetHeight}px`,
    height: frameHeight,
    transform:
      aspectRatio > 1
        ? `translateX(${Math.floor((containerWidth - frameWidth) / 2)}px)`
        : `translateY(${Math.floor((containerHeight - frameHeight) / 2)}px)`,
    width: frameWidth,
  };
};

export const getBackgroundPositionFromFrame = ({
  containerWidth,
  containerHeight,
  spritesheet,
  frame,
}: {
  containerWidth: number;
  containerHeight: number;
  spritesheet: ClipSpriteSheet;
  frame: number;
}) => {
  const { backgroundPositionX, backgroundPositionY } = calculateSpritePosition({
    containerHeight,
    containerWidth,
    frame,
    spritesheet,
  });

  return {
    x: backgroundPositionX,
    y: backgroundPositionY,
  };
};
